import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Card,
  Spin,
  notification,
  Timeline,
  Tag,
  Typography,
  Divider,
  Pagination,
  Row,
  Col,
  Button,
  Input,
  Select,
  Empty,
  Table,
  Modal,
} from "antd";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  DownOutlined,
  UpOutlined,
  ThunderboltOutlined,
  DollarOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import api from "../../../services/api";
import { useFreire } from "../../../utils/freireContext";
import Show from "../../../contexts/PermissionContext/Show";
import * as texts from "../locales";
import "./Orders.css";

const { Text, Title } = Typography;
const { Option } = Select;

const CONTENT_FILTERS = {
  ALL: "all",
  ORDER: "order",
  CHARGES: "charges",
  PAYMENTS: "payments",
};

const getStatusColor = (status) => {
  const statusColors = {
    OPEN: "blue",
    CLOSED: "red",
    FINISHED: "green",
    PENDING: "orange",
    PROCESSING: "cyan",
    COMPLETED: "volcano",
    FAILED: "magenta",
    REFUNDED: "warning",
  };
  return statusColors[status] || "default";
};

const getStatusIcon = (status) => {
  const statusIcons = {
    OPEN: <ClockCircleOutlined />,
    CLOSED: <CloseCircleOutlined />,
    FINISHED: <CheckCircleOutlined />,
    PENDING: <ClockCircleOutlined />,
    PROCESSING: <SyncOutlined spin />,
    COMPLETED: <CheckCircleOutlined />,
    FAILED: <CloseCircleOutlined />,
    REFUNDED: <SyncOutlined />,
  };
  return statusIcons[status] || <ClockCircleOutlined />;
};

const normalizeText = (value) =>
  value === undefined || value === null ? "" : String(value).toLowerCase();

const formatCurrency = (value) => {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value / 100);
};

const getItemValueInCents = (itemData) => {
  if (!itemData) {
    return 0;
  }

  if (typeof itemData.value === "number") {
    return itemData.value;
  }

  if (typeof itemData.chargeValue === "number") {
    return itemData.chargeValue;
  }

  return 0;
};

const getRefundedValueInCents = (itemData) => {
  const candidates = [
    itemData?.refundedValue,
    itemData?.refundValue,
    itemData?.refund_value,
  ];

  const refundedValue = candidates.find(
    (value) => typeof value === "number" && Number.isFinite(value)
  );

  return typeof refundedValue === "number" ? refundedValue : null;
};

const isGuaranteeChargeItem = (item) => {
  if (item?.item_type !== "CHARGER_HISTORY" || !item?.itemData) {
    return false;
  }

  const chargeData = item.itemData?.chargerHistory || item.itemData;
  return chargeData?.paymentMethodConfig?.guarantee === true;
};

const isWalletDebitItem = (item) => {
  const normalizedType = normalizeText(item?.itemData?.type)
    .replaceAll("_", "-")
    .replaceAll(/\s+/g, "-");

  return (
    item?.item_type === "PAYMENT_HISTORY" &&
    normalizedType === "wallet-debit"
  );
};

const isRefundedStatus = (itemData) => {
  const status = normalizeText(itemData?.status);

  return (
    status === "refunded" ||
    status === "refunding" ||
    itemData?.hasRefund === true
  );
};

const isFullyRefunded = (itemData) => {
  if (!isRefundedStatus(itemData)) {
    return false;
  }

  const refundedValue = getRefundedValueInCents(itemData);
  const originalValue = itemData?.originalPaidValue || getItemValueInCents(itemData);

  if (refundedValue === null || originalValue === 0) {
    return true;
  }

  return refundedValue >= originalValue;
};

const getSourceItemId = (item) => {
  const itemData = item?.itemData;
  const candidates = [
    itemData?._id,
    itemData?.id,
    itemData?.paymentHistoryId,
    itemData?.paymentHistoryID,
    itemData?.payment_history_id,
    itemData?.chargerHistoryId,
    itemData?.chargerHistoryID,
    itemData?.charger_history_id,
    itemData?.historyId,
    itemData?.historyID,
    itemData?.history_id,
    item?.itemId,
    item?.itemID,
    item?.item_id,
  ];

  const id = candidates.find(
    (value) => value !== undefined && value !== null && String(value).trim()
  );

  return id ? String(id) : null;
};

const matchesItemBySearch = (item, searchTerm) => {
  if (!searchTerm) {
    return true;
  }

  const sourceId = getSourceItemId(item);
  const searchableValues = [
    item?._id,
    item?.item_type,
    item?.entry_type,
    sourceId,
    item?.itemData?.status,
  ];

  return searchableValues.some((value) =>
    normalizeText(value).includes(searchTerm)
  );
};

const matchesItemBySourceIdFilters = (
  item,
  paymentHistoryIdFilter,
  chargeHistoryIdFilter
) => {
  if (!paymentHistoryIdFilter && !chargeHistoryIdFilter) {
    return true;
  }

  const sourceId = normalizeText(getSourceItemId(item));
  const isPayment = item?.item_type === "PAYMENT_HISTORY";
  const isCharge = item?.item_type === "CHARGER_HISTORY";

  if (isPayment) {
    return !paymentHistoryIdFilter || sourceId.includes(paymentHistoryIdFilter);
  }

  if (isCharge) {
    return !chargeHistoryIdFilter || sourceId.includes(chargeHistoryIdFilter);
  }

  return false;
};

const matchesOrderBySourceIdFilters = (
  order,
  paymentHistoryIdFilter,
  chargeHistoryIdFilter
) => {
  if (!paymentHistoryIdFilter && !chargeHistoryIdFilter) {
    return true;
  }

  const items = Array.isArray(order?.items) ? order.items : [];
  const matchesPaymentHistoryId =
    !paymentHistoryIdFilter ||
    items.some(
      (item) =>
        item?.item_type === "PAYMENT_HISTORY" &&
        normalizeText(getSourceItemId(item)).includes(paymentHistoryIdFilter)
    );
  const matchesChargeHistoryId =
    !chargeHistoryIdFilter ||
    items.some(
      (item) =>
        item?.item_type === "CHARGER_HISTORY" &&
        normalizeText(getSourceItemId(item)).includes(chargeHistoryIdFilter)
    );

  return matchesPaymentHistoryId && matchesChargeHistoryId;
};

const filterItemsByContent = (items, contentFilter) => {
  if (!Array.isArray(items)) {
    return [];
  }

  if (contentFilter === CONTENT_FILTERS.CHARGES) {
    return items.filter((item) => item.item_type === "CHARGER_HISTORY");
  }

  if (contentFilter === CONTENT_FILTERS.PAYMENTS) {
    return items.filter((item) => item.item_type === "PAYMENT_HISTORY");
  }

  return items;
};

const getItemCreatedAtTimestamp = (item) => {
  const createdAt = item?.itemData?.createdAt || item?.createdAt;

  if (!createdAt) {
    return 0;
  }

  const timestamp = new Date(createdAt).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const sortItemsByCreatedAt = (items) => {
  const safeItems = Array.isArray(items) ? [...items] : [];

  // Ordem cronológica por criação (mais antigo -> mais recente)
  safeItems.sort(
    (firstItem, secondItem) =>
      getItemCreatedAtTimestamp(firstItem) - getItemCreatedAtTimestamp(secondItem)
  );

  return safeItems;
};


const isOrderAlreadyAdded = (orderId, mergedOrders) => 
 mergedOrders.some((existingOrder) => existingOrder?._id === orderId);

// Valor que ainda vale para a conciliação: estorno total não conta,
// estorno parcial conta apenas a parte não estornada.
const getEffectivePaymentValueInCents = (itemData) => {
  const value = getItemValueInCents(itemData);

  if (!isRefundedStatus(itemData)) {
    return value;
  }

  const refundedValue = getRefundedValueInCents(itemData);
  const isPartialRefund =
    typeof refundedValue === "number" &&
    refundedValue > 0 &&
    refundedValue < value;

  return isPartialRefund ? value - refundedValue : 0;

};

const getOrderReconciliation = (items) => {
  const safeItems = Array.isArray(items) ? items : [];

  const totals = safeItems.reduce(
    (acc, item) => {
          const isPaymentItem = item?.item_type === "PAYMENT_HISTORY";
      const value = isPaymentItem
        ? getEffectivePaymentValueInCents(item?.itemData)
        : getItemValueInCents(item?.itemData);
      const itemType = item?.itemData?.type;
      const isWalletDebit = isWalletDebitItem(item);
      const isChargeItem = item?.item_type === "CHARGER_HISTORY";
      const fullyRefunded = isFullyRefunded(item?.itemData);

      if (fullyRefunded) {
        return acc;
      }

      if (isWalletDebit) {
        acc.credits += value;
        return acc;
      }

      if (isChargeItem) {
        acc.charges += value;
        if (isGuaranteeChargeItem(item)) {
          acc.guaranteeCharges += value;
        }
      }

      if (item?.entry_type === "debit") {
        acc.debits += value;
      }

      if (item?.entry_type === "credit") {
        acc.credits += value;
        if (itemType === "guarantee-credit") {
          acc.guaranteeCredits += value;
        }
      }

      return acc;
    },
    { debits: 0, credits: 0, charges: 0, guaranteeCharges: 0, guaranteeCredits: 0 }
  );

  const reconciliationResult = totals.credits - totals.charges;
  const guaranteeReconciliationResult = totals.guaranteeCredits - totals.guaranteeCharges;

  return {
    debits: totals.debits,
    credits: totals.credits,
    guaranteeCredits: totals.guaranteeCredits,
    charges: totals.charges,
    guaranteeCharges: totals.guaranteeCharges,
    reconciliationResult,
    guaranteeReconciliationResult,
    isBalanced: reconciliationResult === 0,
    isGuaranteeBalanced: guaranteeReconciliationResult === 0,
  };
};

const formatTimestamp = (value) => {
  if (!value) {
    return "N/A";
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "N/A" : date.toLocaleString();
};

const renderReconciliationResult = (result, isBalanced) => {
  const isNegative = result < 0;
  const label = isNegative ? "Saldo negativo de" : isBalanced ? "Resultado: " : "Saldo positivo de";
  const color = isNegative ? "#cf1322" : "#389e0d";

  return (
    <div>
      <Text type="secondary">
        {label}{" "}
        <Text style={{ color, fontWeight: 600 }}>
          {formatCurrency(Math.abs(result))}
        </Text>
      </Text>
    </div>
  );
};

const renderItemsTable = (items, itemType, onDetailsClick) => {
  const filteredItems = items.filter((item) => item.item_type === itemType);

  if (filteredItems.length === 0) {
    return null;
  }

  const renderTypeCell = (entryType, itemData, isPayment) => {
    let typeLabel;
    let color;

    if (isPayment) {
      const paymentType = itemData?.type;
      typeLabel = paymentType || entryType;
      color = entryType === "credit" ? "#389e0d" : "#cf1322";
    } else {
      if (entryType === "info") {
        typeLabel = "Recarga";
        color = "#cf1322";
      } else if (entryType === "credit") {
        typeLabel = "Crédito";
        color = "#389e0d";
      } else if (entryType === "debit") {
        typeLabel = "Débito";
        color = "#cf1322";
      } else {
        typeLabel = entryType;
        color = "#666";
      }
    }

    return <Text style={{ color, fontWeight: 600 }}>{typeLabel}</Text>;
  };

  const columns = [
    {
      title: "Item ID",
      dataIndex: "_id",
      key: "_id",
      render: (id) => <Text copyable>{id}</Text>,
      width: "15%",
    },
    {
      title: "Tipo",
      key: "type",
      render: (_, record) => renderTypeCell(record.entry_type, record.itemData, itemType === "PAYMENT_HISTORY"),
      width: "10%",
    },
    {
      title: "Valor",
      dataIndex: ["itemData"],
      key: "value",
      render: (itemData) => {
        const value = getItemValueInCents(itemData);
        const refunded = isRefundedStatus(itemData);
        return (
          <Text style={{ color: refunded ? "#8c8c8c" : "#000", textDecoration: refunded ? "line-through" : "none" }}>
            {formatCurrency(value)}
          </Text>
        );
      },
      width: "12%",
    },
    {
      title: itemType === "CHARGER_HISTORY" ? "Charge ID" : "Payment ID",
      dataIndex: "itemData",
      key: "sourceId",
      render: (_, record) => {
        const sourceId = getSourceItemId(record);
        return <Text copyable>{sourceId || "N/A"}</Text>;
      },
      width: "15%",
    },
    {
      title: "Criado",
      dataIndex: ["itemData", "createdAt"],
      key: "createdAt",
      render: (createdAt) => formatTimestamp(createdAt),
      width: "15%",
    },
    {
      title: "Atualizado",
      dataIndex: ["itemData", "updatedAt"],
      key: "updatedAt",
      render: (updatedAt) => formatTimestamp(updatedAt),
      width: "15%",
    },
    {
      title: "Ação",
      key: "action",
      render: (_, record) => {
        if (!record.itemData) {
          return <Text type="danger">sem dados</Text>;
        }
        return (
          <Button
            type="link"
            size="small"
            onClick={() => onDetailsClick(record.itemData)}
          >
            Detalhes
          </Button>
        );
      },
      width: "8%",
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={filteredItems}
      rowKey={(record) => record._id}
      size="small"
      pagination={false}
      scroll={{ x: 800 }}
    />
  );
};

const renderOrderItems = (items, onDetailsClick) => {
  const hasCharges = items.some((item) => item.item_type === "CHARGER_HISTORY");
  const hasPayments = items.some((item) => item.item_type === "PAYMENT_HISTORY");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {hasCharges && (
        <div>
          <Title level={5} style={{ marginBottom: 12 }}>
            <ThunderboltOutlined style={{ marginRight: 8, color: "#cf1322" }} />
            Recargas
          </Title>
          {renderItemsTable(items, "CHARGER_HISTORY", onDetailsClick)}
        </div>
      )}
      {hasPayments && (
        <div>
          <Title level={5} style={{ marginBottom: 12 }}>
            <DollarOutlined style={{ marginRight: 8, color: "#389e0d" }} />
            Pagamentos
          </Title>
          {renderItemsTable(items, "PAYMENT_HISTORY", onDetailsClick)}
        </div>
      )}
    </div>
  );
};

const OrderItemsSection = ({ items }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [detailsModalVisible, setDetailsModalVisible] = useState(false);
  const [selectedItemData, setSelectedItemData] = useState(null);
  const safeItems = Array.isArray(items) ? items : [];
  const hasItems = safeItems.length > 0;

  const openDetailsModal = (itemData) => {
    setSelectedItemData(itemData);
    setDetailsModalVisible(true);
  };

  return (
    <div className="order-items-section">
      <Button
        type="link"
        onClick={() => setIsExpanded(!isExpanded)}
        icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
        className="order-items-section__toggle"
      >
        {isExpanded ? "Ocultar itens" : `Mostrar itens (${safeItems.length})`}
      </Button>
      {isExpanded && (
        <div className="order-items-section__content">
          {hasItems ? (
            renderOrderItems(safeItems, openDetailsModal)
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Sem itens para os filtros atuais"
            />
          )}
        </div>
      )}
      <Modal
        visible={detailsModalVisible}
        onCancel={() => setDetailsModalVisible(false)}
        footer={null}
        width={800}
        title="Detalhes do Item"
        destroyOnClose
      >
        <pre
          style={{
            background: "#f5f5f5",
            padding: "12px",
            borderRadius: "4px",
            marginTop: "8px",
            fontSize: "12px",
            overflow: "auto",
            border: "1px solid #f0f0f0",
            maxHeight: "60vh",
          }}
        >
          {JSON.stringify(selectedItemData, null, 2)}
        </pre>
      </Modal>
    </div>
  );
};

const OrderStatusHistory = ({ history }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const safeHistory = Array.isArray(history) ? history : [];

  const columns = useMemo(
    () => [
      {
        title: "De",
        dataIndex: "old_status",
        key: "old_status",
        render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      },
      {
        title: "Para",
        dataIndex: "new_status",
        key: "new_status",
        render: (status) => <Tag color={getStatusColor(status)}>{status}</Tag>,
      },
      {
        title: "Descrição",
        dataIndex: "description",
        key: "description",
        render: (description) => (
          <Text type="secondary">{description || "Sem descrição"}</Text>
        ),
      },
      {
        title: "Data",
        dataIndex: "createdAt",
        key: "createdAt",
        render: (createdAt) => formatTimestamp(createdAt),
      },
    ],
    []
  );

  return (
    <div className="order-status-history">
      <Button
        type="link"
        onClick={() => setIsExpanded(!isExpanded)}
        icon={isExpanded ? <UpOutlined /> : <DownOutlined />}
        className="order-status-history__toggle"
      >
        {isExpanded
          ? "Ocultar histórico de status"
          : `Mostrar histórico de status (${safeHistory.length})`}
      </Button>
      {isExpanded && (
        <Table
          size="small"
          rowKey={(record) =>
            record._id ||
            `${record.old_status}-${record.new_status}-${record.createdAt}`
          }
          dataSource={safeHistory}
          columns={columns}
          pagination={false}
          className="order-status-history__table"
          locale={{ emptyText: "Sem histórico de status" }}
        />
      )}
    </div>
  );
};

const Orders = ({ userID }) => {
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [actionLoadingOrderId, setActionLoadingOrderId] = useState(null);
  const [actionModalVisible, setActionModalVisible] = useState(false);
  const [actionModalReason, setActionModalReason] = useState("");
  const [pendingOrderAction, setPendingOrderAction] = useState(null);
  const lastServerPageSizeRef = useRef(10);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({
    search: "",
    status: "all",
    content: CONTENT_FILTERS.ALL,
    paymentHistoryId: "",
    chargeHistoryId: "",
  });
  const { freire } = useFreire();

  const hasActiveFilters = useMemo(() => {
    const normalizedSearch = normalizeText(filters.search).trim();
    const paymentHistoryIdFilter = normalizeText(filters.paymentHistoryId).trim();
    const chargeHistoryIdFilter = normalizeText(filters.chargeHistoryId).trim();

    return Boolean(
      normalizedSearch ||
        filters.status !== "all" ||
        filters.content !== CONTENT_FILTERS.ALL ||
        paymentHistoryIdFilter ||
        chargeHistoryIdFilter
    );
  }, [filters]);

  const fetchOrders = useCallback(
    async (page = 1, limit = 10) => {
      try {
        setLoading(true);
        const response = await api.get(
          `/users/${userID}/orders?page=${page}&limit=${limit}`
        );

        if (response && response.data) {
          lastServerPageSizeRef.current = response.data.pagination?.limit || 10;
          setOrders(response.data.orders || []);
          setPagination({
            current: response.data.pagination?.page || 1,
            pageSize: response.data.pagination?.limit || 10,
            total: response.data.pagination?.total || 0,
          });
        } else {
          lastServerPageSizeRef.current = 10;
          setOrders([]);
          setPagination({
            current: 1,
            pageSize: 10,
            total: 0,
          });
        }
      } catch (error) {
        lastServerPageSizeRef.current = 10;
        notification.error({
          message: freire(texts.OOPS),
          description: freire(texts.ERROR_GET_ORDERS),
        });
        setOrders([]);
        setPagination({
          current: 1,
          pageSize: 10,
          total: 0,
        });
      } finally {
        setLoading(false);
      }
    },
    [userID, freire]
  );

  const fetchAllOrders = useCallback(async () => {
    const limit = 100;

    try {
      setLoading(true);

      const firstResponse = await api.get(
        `/users/${userID}/orders?page=1&limit=${limit}`
      );

      const firstOrders = firstResponse?.data?.orders || [];
      const firstPagination = firstResponse?.data?.pagination || {};
      const totalOrders = firstPagination.total || firstOrders.length;
      const totalPages = Math.max(
        1,
        Math.ceil(totalOrders / (firstPagination.limit || limit))
      );

      if (totalPages === 1) {
        setAllOrders(firstOrders);
        return;
      }

      const remainingResponses = await Promise.all(
        Array.from({ length: totalPages - 1 }, (_, index) =>
          api.get(`/users/${userID}/orders?page=${index + 2}&limit=${limit}`)
        )
      );

      const mergedOrders = [...firstOrders];
      remainingResponses.forEach((response) => {
        (response?.data?.orders || []).forEach((order) => {
          if (!isOrderAlreadyAdded(order?._id, mergedOrders)) {
            mergedOrders.push(order);
          }
        });
      });

      setAllOrders(mergedOrders);
    } catch (error) {
      console.error("Failed to fetch all orders:", error);
      notification.error({
        message: freire(texts.OOPS),
        description: freire(texts.ERROR_GET_ORDERS),
      });
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  }, [userID, freire]);

  useEffect(() => {
    if (userID) {
      if (hasActiveFilters) {
        fetchAllOrders();
      } else {
        setAllOrders([]);
        fetchOrders(1, lastServerPageSizeRef.current);
      }
    }
  }, [userID, hasActiveFilters, fetchAllOrders, fetchOrders]);

  useEffect(() => {
    setPagination((current) => ({
      ...current,
      current: 1,
    }));
  }, [
    filters.search,
    filters.status,
    filters.content,
    filters.paymentHistoryId,
    filters.chargeHistoryId,
  ]);

  const sourceOrders = hasActiveFilters ? allOrders : orders;

  const availableStatuses = useMemo(() => {
    const statuses = new Set(
      sourceOrders
        .map((order) => order.status)
        .filter(Boolean)
    );

    return Array.from(statuses);
  }, [sourceOrders]);

  const processedOrders = useMemo(() => {
    const normalizedSearch = normalizeText(filters.search).trim();
    const paymentHistoryIdFilter = normalizeText(filters.paymentHistoryId).trim();
    const chargeHistoryIdFilter = normalizeText(filters.chargeHistoryId).trim();
    const hasSourceIdFilters = Boolean(paymentHistoryIdFilter || chargeHistoryIdFilter);

    return sourceOrders
      .filter((order) => {
        if (
          !matchesOrderBySourceIdFilters(
            order,
            paymentHistoryIdFilter,
            chargeHistoryIdFilter
          )
        ) {
          return false;
        }

        if (filters.status === "all") {
          return true;
        }

        return order.status === filters.status;
      })
      .map((order) => {
        const itemsByContent = filterItemsByContent(order.items, filters.content);
        const filteredItems = itemsByContent.filter((item) => {
          if (
            !matchesItemBySourceIdFilters(
              item,
              paymentHistoryIdFilter,
              chargeHistoryIdFilter
            )
          ) {
            return false;
          }

          if (!normalizedSearch) {
            return true;
          }

          return matchesItemBySearch(item, normalizedSearch);
        });

        const orderMatchesMetadata =
          !normalizedSearch ||
          normalizeText(order._id).includes(normalizedSearch) ||
          normalizeText(order.status).includes(normalizedSearch);
        let itemsToRender = filteredItems;

        const isOrderOnlyView = filters.content === CONTENT_FILTERS.ORDER;
        let shouldIncludeOrder = false;

        if (isOrderOnlyView) {
          shouldIncludeOrder = hasSourceIdFilters
            ? filteredItems.length > 0
            : orderMatchesMetadata || filteredItems.length > 0;
          itemsToRender = [];
        } else if (hasSourceIdFilters) {
          shouldIncludeOrder = filteredItems.length > 0;
        } else if (normalizedSearch) {
          shouldIncludeOrder = orderMatchesMetadata || filteredItems.length > 0;
          if (orderMatchesMetadata && filteredItems.length === 0) {
            itemsToRender = itemsByContent;
          }
        } else {
          shouldIncludeOrder = true;
        }

        if (!shouldIncludeOrder) {
          return null;
        }

        if (
          filters.content !== CONTENT_FILTERS.ALL &&
          filters.content !== CONTENT_FILTERS.ORDER &&
          !isOrderOnlyView &&
          itemsToRender.length === 0
        ) {
          return null;
        }

        return {
          ...order,
          filteredItems: isOrderOnlyView ? [] : sortItemsByCreatedAt(itemsToRender),
          isOrderOnlyView,
        };
      })
      .filter((order) => order !== null);
  }, [sourceOrders, filters]);

  const visibleOrders = useMemo(() => {
    if (!hasActiveFilters) {
      return processedOrders;
    }

    const start = (pagination.current - 1) * pagination.pageSize;
    const end = start + pagination.pageSize;
    return processedOrders.slice(start, end);
  }, [hasActiveFilters, pagination, processedOrders]);

  const handlePageChange = (page, pageSize) => {
    if (hasActiveFilters) {
      setPagination((current) => ({
        ...current,
        current: page,
        pageSize,
      }));
      return;
    }

    lastServerPageSizeRef.current = pageSize;
    fetchOrders(page, pageSize);
  };

  const getActionLabel = (status) => {
    if (status === "OPEN") return "Fechar";
    if (status === "CLOSED") return "Conciliar";
    if (status === "FINISHED") return "Reconciliar";
    return null;
  };

  const openActionModal = useCallback((orderId) => {
    setPendingOrderAction(orderId);
    setActionModalVisible(true);
    setActionModalReason("");
  }, []);

  const closeActionModal = useCallback(() => {
    setActionModalVisible(false);
    setActionModalReason("");
    setPendingOrderAction(null);
  }, []);

  const confirmOrderAction = useCallback(async () => {
    if (!pendingOrderAction) return;

    setActionLoadingOrderId(pendingOrderAction);
    try {
      await api.post(`/orders/${pendingOrderAction}/trigger-action`, {
        reason: actionModalReason || undefined,
      });
      notification.success({ message: "Ação executada com sucesso" });
      closeActionModal();
      // Refetch
      if (hasActiveFilters) {
        fetchAllOrders();
      } else {
        fetchOrders(pagination.current, pagination.pageSize);
      }
    } catch (error) {
      notification.error({
        message: "Erro ao executar ação",
        description: error?.response?.data?.error || "Erro desconhecido",
      });
    } finally {
      setActionLoadingOrderId(null);
    }
  }, [pendingOrderAction, actionModalReason, hasActiveFilters, fetchAllOrders, fetchOrders, pagination, closeActionModal]);

  const hasOrders = visibleOrders.length > 0;
  const hasAnyOrderFromApi = (hasActiveFilters ? allOrders : orders).length > 0;
  const totalOrders = hasActiveFilters ? processedOrders.length : pagination.total;

  if (loading) {
    return (
      <div
        style={{ display: "flex", justifyContent: "center", padding: "50px" }}
      >
        <Spin size="large" tip={freire(texts.LOADING_ORDERS)} />
      </div>
    );
  }

  return (
    <div>
      <Card className="orders-filter-card">
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Input
              value={filters.search}
              placeholder="Buscar por Order ID, item ID ou status"
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  search: event.target.value,
                }))
              }
            />
          </Col>
          <Col xs={24} md={8}>
            <Select
              value={filters.content}
              style={{ width: "100%" }}
              onChange={(value) =>
                setFilters((current) => ({ ...current, content: value }))
              }
            >
              <Option value={CONTENT_FILTERS.ALL}>Tudo</Option>
              <Option value={CONTENT_FILTERS.ORDER}>Order</Option>
              <Option value={CONTENT_FILTERS.CHARGES}>Cargas</Option>
              <Option value={CONTENT_FILTERS.PAYMENTS}>Payment History</Option>
            </Select>
          </Col>
          <Col xs={24} md={8}>
            <Select
              value={filters.status}
              style={{ width: "100%" }}
              onChange={(value) =>
                setFilters((current) => ({ ...current, status: value }))
              }
            >
              <Option value="all">Todos os status</Option>
              {availableStatuses.map((status) => (
                <Option value={status} key={status}>
                  {status}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>
        <Row gutter={12} style={{ marginTop: 12 }}>
          <Col xs={24} md={12}>
            <Input
              value={filters.paymentHistoryId}
              placeholder="Filtrar por Payment History ID"
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  paymentHistoryId: event.target.value,
                }))
              }
            />
          </Col>
          <Col xs={24} md={12}>
            <Input
              value={filters.chargeHistoryId}
              placeholder="Filtrar por Charge History ID"
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  chargeHistoryId: event.target.value,
                }))
              }
            />
          </Col>
        </Row>
      </Card>
      {hasOrders ? (
        visibleOrders.map((order) => {
          const reconciliation = getOrderReconciliation(order.items);
          const hasGuaranteeCharge = (order.items || []).some(
            isGuaranteeChargeItem
          );

          return (
          <Card key={order._id} style={{ width: "100%", marginBottom: 24 }}>
            <Row type="flex" justify="space-between" align="middle" gutter={16}>
              <Col xs={24} md={12}>
                <Title level={4}>Order #{order._id}</Title>
              </Col>
              <Col xs={24} md={12}>
                <div className="order-header-meta-list">
                  <div className="order-header-meta-item">
                    <Text type="secondary">Status:</Text>
                    <Tag
                      color={getStatusColor(order.status)}
                      icon={getStatusIcon(order.status)}
                    >
                      {order.status}
                    </Tag>
                  </div>
                  <div className="order-header-meta-item">
                    <Text type="secondary">
                      Created: {formatTimestamp(order.createdAt)}
                    </Text>
                  </div>
                  <div className="order-header-meta-item">
                    <Text type="secondary">
                      Updated: {formatTimestamp(order.updatedAt)}
                    </Text>
                  </div>
                </div>
              </Col>
            </Row>

            <Divider style={{ margin: "12px 0" }} />

            <Card size="small" className="orders-reconciliation-card">
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <ThunderboltOutlined style={{ fontSize: 16, color: "#cf1322" }} />
                    <Text type="secondary">Total Recargas</Text>
                  </div>
                  <Title level={4} style={{ margin: 0, color: "#cf1322" }}>
                    {formatCurrency(reconciliation.charges)}
                  </Title>
                </Col>
                <Col xs={24} md={8}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <ThunderboltOutlined style={{ fontSize: 16, color: "#cf1322" }} />
                    <Text type="secondary">Recargas Garantia</Text>
                  </div>
                  <Title level={4} style={{ margin: 0, color: "#cf1322" }}>
                    {formatCurrency(reconciliation.guaranteeCharges)}
                  </Title>
                </Col>
                <Col xs={24} md={8}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    {reconciliation.isGuaranteeBalanced ? (
                      <CheckCircleOutlined style={{ fontSize: 16, color: "#389e0d" }} />
                    ) : (
                      <WarningOutlined style={{ fontSize: 16, color: "#d46b08" }} />
                    )}
                    <Text type="secondary" strong>Resultado Garantia</Text>
                    <Show when="refund-wallet-balance">
                      {getActionLabel(order.status) && (
                        <Button
                          type="primary"
                          size="small"
                          loading={actionLoadingOrderId === order._id}
                          onClick={() => openActionModal(order._id)}
                        >
                          {getActionLabel(order.status)}
                        </Button>
                      )}
                    </Show>
                  </div>
                  <div style={{ marginTop: 8 }}>
                    {renderReconciliationResult(
                      reconciliation.guaranteeReconciliationResult,
                      reconciliation.isGuaranteeBalanced
                    )}
                  </div>
                </Col>
              </Row>
              <Row gutter={16} style={{ marginTop: 16 }}>
                <Col xs={24} md={8}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <DollarOutlined style={{ fontSize: 16, color: "#389e0d" }} />
                    <Text type="secondary">Total Créditos</Text>
                  </div>
                  <Title level={4} style={{ margin: 0, color: "#389e0d" }}>
                    {formatCurrency(reconciliation.credits)}
                  </Title>
                </Col>
                <Col xs={24} md={8}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                    <DollarOutlined style={{ fontSize: 16, color: "#389e0d" }} />
                    <Text type="secondary">Créditos Garantia</Text>
                  </div>
                  <Title level={4} style={{ margin: 0, color: "#389e0d" }}>
                    {formatCurrency(reconciliation.guaranteeCredits)}
                  </Title>
                </Col>
                <Col xs={24} md={8} />
              </Row>
            </Card>

            <Timeline>
              {!order.isOrderOnlyView && (
                <Timeline.Item color="green">
                  <Text strong>Items</Text>
                  <div style={{ marginTop: 8 }}>
                    <OrderItemsSection items={order.filteredItems} />
                  </div>
                </Timeline.Item>
              )}

              <Timeline.Item color="purple">
                <Text strong>Status History</Text>
                <div style={{ marginTop: 16 }}>
                  <OrderStatusHistory history={order.status_history} />
                </div>
              </Timeline.Item>
            </Timeline>

            {hasGuaranteeCharge && (
              <div className="order-precharge-alert">
                <Tag color="gold">Pré cobrança</Tag>
                <Text strong>
                  Esta order possui recarga do tipo guarantee (pré cobrança).
                </Text>
              </div>
            )}
          </Card>
          );
        })
      ) : (
        <div
          style={{ display: "flex", justifyContent: "center", padding: "50px" }}
        >
          {hasAnyOrderFromApi ? (
            <Empty
              description="Nenhuma order encontrada com os filtros aplicados."
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          ) : (
            <Text>No orders found.</Text>
          )}
        </div>
      )}
      <Row type="flex" justify="center" style={{ marginTop: 24 }}>
        <Col>
          <Pagination
            current={pagination.current}
            pageSize={pagination.pageSize}
            total={totalOrders}
            onChange={handlePageChange}
            showSizeChanger
            showTotal={(total, range) =>
              `${range[0]}-${range[1]} of ${total} orders`
            }
          />
        </Col>
      </Row>

      <Modal
        title="Confirmar ação"
        visible={actionModalVisible}
        onCancel={closeActionModal}
        onOk={confirmOrderAction}
        okText="Confirmar"
        cancelText="Cancelar"
        confirmLoading={actionLoadingOrderId === pendingOrderAction}
      >
        <div style={{ marginBottom: 16 }}>
          <Text>
            Você está prestes a executar uma ação nesta order. Esta ação será
            registrada no histórico com seu email como responsável.
          </Text>
        </div>
        <div>
          <Text strong>Motivo (opcional):</Text>
          <Input.TextArea
            value={actionModalReason}
            onChange={(e) => setActionModalReason(e.target.value)}
            placeholder="Descreva o motivo dessa ação (opcional)"
            rows={3}
            style={{ marginTop: 8 }}
          />
        </div>
      </Modal>
    </div>
  );
};

export default Orders;
